<script setup>
  import { reactive, ref, watch } from "vue"
  import Swal from "sweetalert2"
  import api from "../../api/axios.js"
  import { destinosDisponibles, paisesDestinos } from "@/utils/destinos";
  import { nombreOrigen as nombreOrigenOtro } from "@/utils/origenes";
  import FechaDatePicker from "@/components/cotizador/FechaDatePicker.vue";
  import { es } from "date-fns/locale";

  // Mismas opciones de origen que "Mis Cotizaciones" (List.vue).
  const origenesFiltro = [
    { value: "BOG", label: "Bogotá" },
    { value: "CLO", label: "Cali" },
    { value: "MDE", label: "Medellín" },
    { value: "CTG", label: "Cartagena" },
    { value: "BAQ", label: "Barranquilla" },
    { value: "BGA", label: "Bucaramanga" },
    { value: "LET", label: "Leticia" },
    { value: "AXM", label: "Armenia" },
    { value: "PEI", label: "Pereira" },
    { value: "PSO", label: "Pasto" },
    { value: "PPN", label: "Popayán" },
    { value: "SMR", label: "Santa Marta" },
    { value: "MTR", label: "Montería" },
    { value: "RCH", label: "Riohacha" },
    { value: "VVC", label: "Villavicencio" },
    { value: "IBE", label: "Ibagué" },
    { value: "NVA", label: "Neiva" },
    { value: "CUC", label: "Cúcuta" },
    { value: "MZL", label: "Manizales" },
    { value: "VUP", label: "Valledupar" },
    { value: "EYP", label: "Yopal" }
  ];

  const filtros = reactive({
    origin: "",
    destination: "",
    dateFrom: "",
    dateTo: "",
    passengers: ""
  });

  // Igual que en List.vue: al elegir "desde", "hasta" se autocompleta con el
  // día siguiente, pero sigue siendo editable.
  watch(() => filtros.dateFrom, (nuevaFecha) => {
    if (!nuevaFecha) return
    const f = new Date(nuevaFecha)
    f.setDate(f.getDate() + 1)
    filtros.dateTo = f.toISOString().split("T")[0]
  })

  // No se consulta nada al entrar: los resultados aparecen solo al pulsar
  // "Buscar". `buscado` distingue "aún no se buscó" de "sin resultados".
  const cotizaciones = ref([])
  const total = ref(0)
  const cargando = ref(false)
  const cargandoMas = ref(false)
  const buscado = ref(false)
  // Filtros con los que se hizo la última búsqueda, para que "Cargar más"
  // pagine sobre esa misma búsqueda aunque el usuario ya haya tocado los selects.
  let filtrosBuscados = {}

  async function pedirPagina(offset) {
    const { data } = await api.get("/quotes/buscador", { params: { ...filtrosBuscados, offset } })
    total.value = data.total
    return data.cotizaciones
  }

  async function buscar() {
    filtrosBuscados = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== "" && v !== null))
    cargando.value = true
    try {
      cotizaciones.value = await pedirPagina(0)
      buscado.value = true
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "No se pudo buscar cotizaciones",
        text: err.response?.data?.error || "Error de conexión con el servidor."
      })
    } finally {
      cargando.value = false
    }
  }

  async function cargarMas() {
    cargandoMas.value = true
    try {
      cotizaciones.value.push(...await pedirPagina(cotizaciones.value.length))
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "No se pudieron cargar más cotizaciones",
        text: err.response?.data?.error || "Error de conexión con el servidor."
      })
    } finally {
      cargandoMas.value = false
    }
  }

  function limpiar() {
    Object.assign(filtros, { origin: "", destination: "", dateFrom: "", dateTo: "", passengers: "" })
    cotizaciones.value = []
    total.value = 0
    buscado.value = false
  }

  const nombreOrigen = (code) => origenesFiltro.find((o) => o.value === code)?.label || nombreOrigenOtro(code)
  const nombreDestino = (code) => destinosDisponibles.find((d) => d.value === code)?.label || code

  // PERU se guarda en la BD como "Perú con guia" / "Perú sin guia" (ver
  // guardarCotizacion en el back), así que no aparece tal cual en paisesDestinos.
  const banderaDestino = (code) => {
    const iso = paisesDestinos[code] || (code?.startsWith("Perú") ? paisesDestinos.PERU : null)
    return iso ? `https://flagcdn.com/w40/${iso}.png` : null
  }

  const formatDate = (value) => {
    if (!value) return ""
    return new Date(value).toLocaleDateString("es-CO", { year: "numeric", month: "2-digit", day: "2-digit" })
  }

  const formatCOP = (value) =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(value || 0)

  const getDriveDownloadUrl = (url) => {
    const match = url.match(/\/d\/([^/]+)/)
    return match ? `https://drive.google.com/uc?export=download&id=${match[1]}` : url
  }
</script>

<template>
  <div>
    <div class="card shadow-sm mb-4">
      <div class="card-header pb-0">
        <h5 class="mb-0">
          <i class="bi bi-search me-2"></i>
          Buscador Cotizaciones
        </h5>
        <p class="text-sm text-muted mb-0 mt-1">
          Busca entre las cotizaciones de todos los asesores creadas en los últimos 15 días.
        </p>
      </div>

      <div class="card-body">
        <form class="filtros-buscador p-3" @submit.prevent="buscar">
          <div class="row g-2 g-lg-3 align-items-end">

            <div class="col-12 col-sm-6 col-lg-2">
              <label class="form-label small text-muted mb-1">Origen</label>
              <select v-model="filtros.origin" class="form-select form-select-sm" style="height: 40px;">
                <option value="">Todos</option>
                <option v-for="o in origenesFiltro" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>

            <div class="col-12 col-sm-6 col-lg-2">
              <label class="form-label small text-muted mb-1">Destino</label>
              <select v-model="filtros.destination" class="form-select form-select-sm" style="height: 40px;">
                <option value="">Todos</option>
                <option v-for="d in destinosDisponibles" :key="d.value" :value="d.value">{{ d.label }}</option>
              </select>
            </div>

            <div class="col-12 col-sm-8 col-lg-3">
              <label class="form-label small text-muted mb-1">Fechas del viaje</label>
              <div class="row g-2">
                <div class="col-6">
                  <FechaDatePicker v-model="filtros.dateFrom" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
                    format="yyyy-MM-dd" :enable-time-picker="false" auto-apply placeholder="Desde" />
                </div>
                <div class="col-6">
                  <FechaDatePicker v-model="filtros.dateTo" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
                    format="yyyy-MM-dd" :enable-time-picker="false" :min-date="filtros.dateFrom || undefined" auto-apply
                    placeholder="Hasta" />
                </div>
              </div>
            </div>

            <div class="col-12 col-sm-4 col-lg-1">
              <label class="form-label small text-muted mb-1">Pasajeros</label>
              <input v-model="filtros.passengers" type="number" min="0" class="form-control form-control-sm"
                style="height: 40px;">
            </div>

            <div class="col-12 col-lg-4">
              <div class="d-flex gap-2">
                <button type="submit" class="btn btn-sm btn-dark flex-fill mb-0" :disabled="cargando" style="height: 40px;">
                  <span v-if="cargando" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  <i v-else class="bi bi-search me-1"></i>Buscar
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary flex-fill mb-0" @click="limpiar"
                  style="height: 40px;">
                  <i class="bi bi-x-lg me-1"></i>Limpiar
                </button>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>

    <div v-if="!buscado" class="text-center text-muted py-5">
      <i class="bi bi-funnel fs-2 d-block mb-2"></i>
      Elige los filtros y pulsa <b>Buscar</b> para ver las cotizaciones.
    </div>

    <div v-else-if="cotizaciones.length === 0" class="text-center text-muted py-5">
      <i class="bi bi-inbox fs-2 d-block mb-2"></i>
      No hay cotizaciones de los últimos 15 días que coincidan con esos filtros.
    </div>

    <template v-else>
      <p class="text-sm text-muted mb-3">
        Mostrando {{ cotizaciones.length }} de {{ total }} cotizaciones
      </p>

      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xxl-4 g-3">
        <div v-for="q in cotizaciones" :key="q.id" class="col">
          <div class="card h-100 shadow-sm cotizacion-card">
            <div class="card-body d-flex flex-column">

              <div class="d-flex align-items-center gap-2 mb-2">
                <img v-if="banderaDestino(q.destination)" :src="banderaDestino(q.destination)"
                  :alt="nombreDestino(q.destination)" class="bandera" />
                <h6 class="mb-0 text-truncate" :title="nombreDestino(q.destination)">
                  {{ nombreDestino(q.destination) }}
                </h6>
              </div>

              <p class="text-sm mb-1">
                <i class="bi bi-airplane me-1 text-muted"></i>
                Desde <b>{{ nombreOrigen(q.origin) }}</b>
              </p>
              <p class="text-sm mb-1">
                <i class="bi bi-calendar-event me-1 text-muted"></i>
                {{ formatDate(q.start_date) }} - {{ formatDate(q.end_date) }}
              </p>
              <p class="text-sm mb-1">
                <i class="bi bi-people me-1 text-muted"></i>
                {{ q.passengers }} {{ Number(q.passengers) === 1 ? "pasajero" : "pasajeros" }}
              </p>
              <p v-if="q.telefono" class="text-sm mb-1">
                <i class="bi bi-telephone me-1 text-muted"></i>
                {{ q.telefono }}
              </p>

              <div class="mt-2 mb-3">
                <span class="text-xs text-muted d-block">Precio desde</span>
                <span class="h5 mb-0 precio">{{ formatCOP(q.total_price) }}</span>
              </div>

              <div class="mt-auto d-flex justify-content-between align-items-center gap-2">
                <span class="text-xs text-muted">Creada {{ formatDate(q.created_at) }}</span>
                <div v-if="q.pdf_url" class="d-flex gap-2">
                  <a :href="q.pdf_url" target="_blank" rel="noopener" class="btn btn-sm btn-primary mb-0">Ver PDF</a>
                  <a :href="getDriveDownloadUrl(q.pdf_url)" class="btn btn-sm btn-dark mb-0" title="Descargar PDF">
                    <i class="bi bi-download"></i>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div v-if="cotizaciones.length < total" class="text-center mt-4">
        <button type="button" class="btn btn-outline-dark" :disabled="cargandoMas" @click="cargarMas">
          <span v-if="cargandoMas" class="spinner-border spinner-border-sm me-1" role="status"></span>
          Cargar más
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
  .filtros-buscador {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
  }

  .filtros-buscador .btn-sm {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }

  .bandera {
    width: 28px;
    height: auto;
    border-radius: 3px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
  }

  .precio {
    color: #2dce89;
  }

  .cotizacion-card {
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  .cotizacion-card:hover {
    transform: translateY(-2px);
  }
</style>
